/**
 * Configuration for available languages per multisite.
 * This allows different multisites to have different sets of supported languages.
 */
export const MULTISITE_LANGUAGES: Record<string, string[]> = {
  "albis-distribution": ["de", "en", "zh", "pt", "fr"],
  "mocom": ["de", "en"],
  "krahn-ceramics": ["de", "en"],
  "otto-krahn-group": ["de", "en"],
  "htf": ["de", "en"],
  "wipag": ["de", "en"],
  "krahn-chemie": ["de", "en"],
  "otto-krahn-new-business": ["de", "en"],
  "plgrd": ["de", "en"],
};

/**
 * Default language fallbacks per multisite.
 * Used when browser language doesn't match any available languages.
 */
export const MULTISITE_DEFAULT_LANGUAGE: Record<string, string> = {
  "albis-distribution": "en",
  "mocom": "de",
  "krahn-ceramics": "de",
  "otto-krahn-group": "de",
  "htf": "de",
  "wipag": "de",
  "krahn-chemie": "de",
  "otto-krahn-new-business": "de",
  "plgrd": "de",
};

/**
 * Multisites that use single-tree i18n structure (DefaultI18nContentSupport).
 * These multisites need frontend-based language detection and redirect.
 */
export const SINGLE_TREE_MULTISITES = ["albis-distribution"];

/**
 * Get available languages for a specific multisite.
 */
export function getMultisiteLanguages(multisiteBase: string): string[] {
  return MULTISITE_LANGUAGES[multisiteBase] || ["de", "en"];
}

/**
 * Get default language for a specific multisite.
 */
export function getMultisiteDefaultLanguage(multisiteBase: string): string {
  return MULTISITE_DEFAULT_LANGUAGE[multisiteBase] || "en";
}

/**
 * Check if a multisite uses single-tree i18n structure.
 */
export function isSingleTreeMultisite(multisiteBase: string): boolean {
  return SINGLE_TREE_MULTISITES.includes(multisiteBase);
}

/**
 * Detects the best matching language from browser preferences.
 * 
 * @param availableLanguages - Array of available language codes for the multisite
 * @param fallbackLanguage - Fallback language if no match is found
 * @returns The best matching language code
 */
export function detectBrowserLanguage(
  availableLanguages: string[],
  fallbackLanguage: string = "en"
): string {
  // Get browser languages in order of preference
  const browserLanguages = navigator.languages || [navigator.language];

  for (const browserLang of browserLanguages) {
    // Extract primary language code (e.g., "en-US" -> "en", "zh-CN" -> "zh")
    const langCode = browserLang.toLowerCase().split("-")[0];
    
    if (availableLanguages.includes(langCode)) {
      return langCode;
    }
  }

  // No match found, return fallback
  return fallbackLanguage;
}

/**
 * Check if the current URL already contains a language code.
 * 
 * @param pathname - The URL pathname to check
 * @param multisiteBase - The multisite base path
 * @param availableLanguages - Available languages for this multisite
 * @returns True if language is present in URL
 */
export function hasLanguageInUrl(
  pathname: string,
  multisiteBase: string,
  availableLanguages: string[]
): boolean {
  // For production (domain-based): /en/about or /de/about
  // For development (path-based): /albis-distribution/en/about
  
  const pathParts = pathname.split("/").filter(Boolean);
  
  if (process.env.NODE_ENV === "production") {
    // In production, language is first segment: /en/about
    return pathParts.length > 0 && availableLanguages.includes(pathParts[0]);
  } else {
    // In development, language is after multisite base: /albis-distribution/en/about
    const multisiteIndex = pathParts.indexOf(multisiteBase);
    if (multisiteIndex >= 0 && multisiteIndex + 1 < pathParts.length) {
      return availableLanguages.includes(pathParts[multisiteIndex + 1]);
    }
    return false;
  }
}
